import { FormattedMessage } from 'react-intl';
import { z } from 'zod';
import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod/v4';

import PersonIcon from '@shieldpay/bumblebee/icons/PersonIcon';

import { Form } from '@shieldpay/bumblebee/form';
import { Heading } from '@shieldpay/bumblebee/heading';
import { InputField } from '@shieldpay/bumblebee/input-field';
import { Column } from '@shieldpay/bumblebee/layout';
import InputFieldMarkdown from '@shieldpay/bumblebee/markdown/input-field.md';
import { Text } from '@shieldpay/bumblebee/text';

import { ContentsTable } from '~/components/contents-table/contents-table';
import { Design } from '~/components/design/design';
import { MdxFormatter } from '~/components/mdx-formatter/mdx-formatter';
import { ComponentPreview } from '~/components/preview/component-preview';
import { createMeta } from '~/utilities/create-meta';

export const meta = createMeta({
  id: 'hive.components.inputField.title',
});

const FieldSchema = z.object({
  liveField1: z.string().max(5, 'too long'),
  liveField2: z.string(),
  liveField3: z.string(),
});

export const InputFieldComponentPage = () => {
  const [form, fields] = useForm({
    defaultNoValidate: false,
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    onValidate: ({ formData }) => {
      return parseWithZod(formData, { schema: FieldSchema });
    },
  });

  return (
    <>
      <Heading>
        <FormattedMessage id="hive.components.inputField.title" />
      </Heading>

      <ContentsTable />

      <ComponentPreview>
        <Heading>Static fields</Heading>
        <Column spacing="basePos8" padding={{ bottom: 'basePos6' }}>
          <InputField
            id="staticField1"
            name="staticField1"
            label="I am a disabled input field"
            placeholder="I am a placeholder"
            disabled
          />
          <InputField
            id="staticField2"
            name="staticField2"
            label="I am a input field with multiple error messages"
            placeholder="I am a placeholder"
            errors={['hey', 'you']}
            aria-invalid
            aria-describedby="error-message-for-liveField5"
          />
          <InputField
            id="staticField3"
            name="staticField3"
            label="I am a disabled input field with an extra icon"
            placeholder="I am a placeholder"
            disabled
          >
            <Column
              alignContent={{ x: 'center', y: 'center' }}
              padding={{ right: 'base' }}
            >
              <PersonIcon />
            </Column>
          </InputField>
          <InputField
            id="staticField3"
            name="staticField3"
            label="I am a readonly input field"
            readOnly
            defaultValue="I am a readonly input field"
          >
            <Column
              alignContent={{ x: 'center', y: 'center' }}
              padding={{ right: 'base' }}
            >
              <PersonIcon />
            </Column>
          </InputField>
        </Column>

        <Heading>Live fields (using Conform library)</Heading>
        <Form {...getFormProps(form)} spacing="basePos8">
          <InputField
            {...getInputProps(fields.liveField1, { type: 'text' })}
            errors={fields.liveField1.errors}
            label="I am a input field with some help"
            helpText="Fill this in for a prize, exceed 5 characters and you will see an error"
          />

          <InputField
            {...getInputProps(fields.liveField2, { type: 'text' })}
            errors={fields.liveField2.errors}
            label="I am a mandatory input field"
          />

          <InputField
            {...getInputProps(fields.liveField3, { type: 'text' })}
            errors={fields.liveField3.errors}
            label="I am a input field with an extra icon"
          >
            <Column
              alignContent={{ x: 'center', y: 'center' }}
              padding={{ right: 'base' }}
            >
              <PersonIcon />
            </Column>
          </InputField>
        </Form>
      </ComponentPreview>

      <Design>
        <Design.Section heading="Usage">
          <Text paragraph>
            The input field component (form field) is the interactive component
            used to capture information. The form field is used with the field
            label component and the helper text component if required.
          </Text>
          <Text paragraph>
            All form fields are based on the ‘master primitive’ component
            (inactive state). If any of the layers or details within the master
            component are changed, all form instances will be updated.
          </Text>
        </Design.Section>
        <Design.Section heading="Anatomy">
          <Text paragraph>
            The form field height is constructed based on the ‘Field input‘ text
            style and padding. It retains a height of 52px across all viewports.
          </Text>
          <Text paragraph>
            The width of the form field is flexible, unless specifically
            constrained by a defined value or alignment. The width of the form
            field should match the width of the field label text box across all
            viewport sizes, unless otherwise specified.
          </Text>
          <Text paragraph>
            The form field has a 1px border with 4px rounded corners to be
            consistent with the buttons.
          </Text>
          <Text paragraph>
            When text is entered/displayed in the form field it uses the ‘Field
            input’ style (16px) across all viewports.
          </Text>
          <Text paragraph>
            It is not proposed to display a disabled state for an empty field.
            If a field is not required it should be hidden.
          </Text>
        </Design.Section>
        <Design.Section heading="Behaviour">
          <Text paragraph>
            The state of the form field changes depending on the actions of the
            user. The structure of how the instances behave are outlined, right.
          </Text>
          <Text paragraph>
            Transition to display a state change with a 0.25 ease.
          </Text>
        </Design.Section>
        <Design.Section heading="Accessibility">
          <Text paragraph>
            The focused state is used to help accessibility and is consistent
            with the focus state used on other components. Use the appropriate
            aria attributes for screen readers where applicable.
          </Text>
          <Text paragraph>
            The ‘Text’ style colour used on entered text in the field, is
            comfortably within the AAA contrast ratio for legibility.
          </Text>
          <Text paragraph>
            Placeholder text will not be used within text fields. Placeholder
            text has to be dark enough to pass AA rating for legibility. It can
            confuse users who may think the field is already completed. When a
            detailed description or an example is required to complement a field
            label, ‘Helper text’ will be used.
          </Text>
        </Design.Section>
      </Design>

      <MdxFormatter md={InputFieldMarkdown} />
    </>
  );
};

export default InputFieldComponentPage;
